<template>
  <section>
    <div class="container px-4 mx-auto">
      <!-- Header -->
      <div
        class="max-w-2xl mx-auto text-center"
        :class="hiddenInfo ? 'mb-0' : 'mb-10'"
      >
        <template v-if="!hiddenInfo">
          <h2
            class="text-3xl font-bold tracking-tight sm:text-4xl text-foreground"
          >
            {{ $t("common.chooseYourPlan") }}
          </h2>
          <p class="mt-4 text-lg text-muted-foreground">
            {{ $t("common.getStartedFree", { credits: TRIAL_CREDITS, days: TRIAL_DAYS }) }}
          </p>
        </template>
      </div>

      <!-- Billing toggle above the cards (sm and up) -->
      <div
        v-if="!modalLifetime"
        class="hidden sm:flex justify-center"
        :class="hiddenInfo ? 'mb-4' : 'mb-10'"
      >
        <BillingToggle v-model="isAnnual" />
      </div>

      <!-- Pricing Cards -->
      <div
        class="grid grid-cols-1 gap-6 mx-auto items-stretch"
        :class="hiddenInfo ? 'max-w-md lg:grid-cols-1' : 'max-w-6xl lg:grid-cols-3'"
      >
        <!-- Free Plan -->
        <div
          v-if="!hiddenInfo"
          class="relative flex flex-col rounded-2xl border bg-card p-8 shadow-sm"
          :class="cardGridClass"
        >
          <h3 class="text-xl font-bold tracking-tight">
            {{ $t("components.pricingSection.free") }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ $t("components.pricingSection.freeDescription") }}
          </p>
          <div class="mt-6 flex items-baseline gap-x-1">
            <span class="text-5xl font-bold tracking-tight">$0</span>
            <span class="text-base font-medium text-muted-foreground">{{
              $t("common.forever")
            }}</span>
          </div>
          <p class="mt-2.5 text-sm text-muted-foreground">
            {{ $t("components.pricingSection.noCardRequired") }}
          </p>
          <Button
            variant="secondary"
            size="lg"
            class="mt-6 w-full rounded-full"
            @click="navigateTo('/auth?view=sign_up')"
          >
            {{ $t("buttons.signUpForFree") }}
          </Button>
          <div class="my-6 h-px bg-border" />
          <ul class="space-y-3.5">
            <li
              v-for="(label, i) in freeFeatures"
              :key="i"
              class="flex items-start gap-3 text-sm leading-snug text-start"
            >
              <span
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
              >
                <Check class="h-3 w-3" />
              </span>
              <span>{{ label }}</span>
            </li>
          </ul>
        </div>

        <!-- Premium Plan -->
        <div
          class="relative flex flex-col rounded-2xl border bg-card"
          :class="
            hiddenInfo
              ? 'border-none p-5'
              : ['border-2 border-primary/40 bg-card p-8 shadow-sm', cardGridClass]
          "
        >
          <span
            v-if="!hiddenInfo"
            class="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-4 py-1 text-xs font-semibold uppercase tracking-wide text-primary-foreground"
          >
            {{ $t("common.mostPopular") }}
          </span>

          <template v-if="!hiddenInfo">
            <h3 class="text-xl font-bold tracking-tight">
              {{ $t("components.pricingSection.premium") }}
            </h3>
            <p class="mt-1 text-sm text-muted-foreground">
              {{ $t("components.pricingSection.unlockAllPremiumFeatures") }}
            </p>
          </template>

          <!-- Billing toggle inside the Pro card on mobile -->
          <div v-if="!modalLifetime" class="flex sm:hidden justify-center" :class="hiddenInfo ? 'mb-1' : 'mt-6'">
            <BillingToggle v-model="isAnnual" />
          </div>

          <!-- Price block follows the selected plan inside the modal -->
          <div
            class="flex items-baseline gap-x-1"
            :class="hiddenInfo ? 'mt-3 justify-center sm:mt-0' : 'mt-6'"
          >
            <template v-if="modalLifetime">
              <span class="font-bold tracking-tight" :class="priceSizeClass">${{ PRO_LIFETIME_PRICE_USD }}</span>
              <span class="text-base font-medium text-muted-foreground lowercase">{{
                $t("components.pricingSection.lifetimeOneTime")
              }}</span>
            </template>
            <template v-else>
              <span class="font-bold tracking-tight" :class="priceSizeClass"
                >${{ isAnnual ? PRO_YEARLY_PRICE_USD : PRO_MONTHLY_PRICE_USD }}</span
              >
              <span class="text-base font-medium text-muted-foreground lowercase"
                >/{{ isAnnual ? $t("common.annual") : $t("common.monthly") }}</span
              >
            </template>
          </div>
          <p v-if="!hiddenInfo || modalLifetime || showTrialInfo" class="mt-2.5 text-sm font-medium text-primary" :class="hiddenInfo ? 'text-center' : ''">
            {{
              modalLifetime
                ? $t("components.pricingSection.lifetimeCredits", { credits: formatNumber(LIFETIME_CREDITS, locale) })
                : showTrialInfo
                  ? $t("components.pricingSection.trialInfo", { credits: TRIAL_CREDITS, days: TRIAL_DAYS })
                  : ""
            }}
          </p>

          <Button
            v-if="!hiddenInfo"
            size="lg"
            class="mt-6 w-full rounded-full"
            @click="navigateTo('/auth?view=sign_up')"
          >
            {{ $t("components.pricingSection.startFreeTrial") }}
          </Button>

          <div class="h-px bg-border" :class="hiddenInfo ? 'my-4' : 'my-6'" />
          <div>
          <ul :class="hiddenInfo ? 'space-y-2' : 'space-y-3.5'">
            <li
              v-for="(label, i) in modalLifetime ? lifetimeFeatures : premiumFeatures"
              :key="i"
              class="flex items-start gap-3 text-sm leading-snug text-start"
            >
              <span
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
              >
                <Check class="h-3 w-3" />
              </span>
              <span>{{ label }}</span>
            </li>
          </ul>
          <p class="text-xs italic text-muted-foreground" :class="hiddenInfo ? 'mt-3' : 'mt-5'">
            *
            {{
              $t("components.pricingSection.buyMoreCredits", {
                credits: BONUS_LIMIT,
                price: BONUS_LIMIT_PRICE,
              })
            }}
          </p>
          <p v-if="modalLifetime" class="mt-2 text-xs text-subtle-foreground">
            {{ $t("components.pricingSection.lifetimeNote") }}
          </p>
          </div>

          <!-- Lifetime as a selectable alternative inside the upgrade modal -->
          <button
            v-if="hiddenInfo"
            type="button"
            class="mt-4 flex w-full items-start gap-3 rounded-xl border p-3 text-start transition-colors"
            :class="plan === 'lifetime' ? 'border-primary bg-primary/5' : 'border-border hover:border-input'"
            :aria-pressed="plan === 'lifetime'"
            @click="plan = plan === 'lifetime' ? 'pro' : 'lifetime'"
          >
            <span
              class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
              :class="plan === 'lifetime' ? 'border-primary bg-primary text-primary-foreground' : 'border-input'"
            >
              <Check v-if="plan === 'lifetime'" class="h-3 w-3" />
            </span>
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-semibold">
                {{ $t("components.pricingSection.lifetimeOrOption", { price: PRO_LIFETIME_PRICE_USD }) }}
              </span>
              <span class="text-xs text-muted-foreground">
                {{ $t("components.pricingSection.lifetimeOrOptionSub", { credits: formatNumber(LIFETIME_CREDITS, locale) }) }}
              </span>
            </span>
          </button>
        </div>

        <!-- Lifetime Plan -->
        <div
          v-if="!hiddenInfo"
          class="relative flex flex-col rounded-2xl border bg-card p-8 shadow-sm"
          :class="cardGridClass"
        >
          <h3 class="text-xl font-bold tracking-tight">
            {{ $t("components.pricingSection.lifetime") }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ $t("components.pricingSection.lifetimeDescription") }}
          </p>
          <div class="mt-6 flex items-baseline gap-x-1">
            <span class="text-5xl font-bold tracking-tight">${{ PRO_LIFETIME_PRICE_USD }}</span>
            <span class="text-base font-medium text-muted-foreground lowercase">{{
              $t("components.pricingSection.lifetimeOneTime")
            }}</span>
          </div>
          <p class="mt-2.5 text-sm font-medium text-primary">
            {{ $t("components.pricingSection.lifetimeCredits", { credits: formatNumber(LIFETIME_CREDITS, locale) }) }}
          </p>
          <Button
            variant="secondary"
            size="lg"
            class="mt-6 w-full rounded-full"
            @click="onLifetimeClick"
          >
            {{ $t("buttons.buyLifetime") }}
          </Button>
          <div class="my-6 h-px bg-border" />
          <div>
          <ul class="space-y-3.5">
            <li
              v-for="(label, i) in lifetimeFeatures"
              :key="i"
              class="flex items-start gap-3 text-sm leading-snug text-start"
            >
              <span
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
              >
                <Check class="h-3 w-3" />
              </span>
              <span>{{ label }}</span>
            </li>
          </ul>
          <p class="mt-5 text-xs italic text-muted-foreground">
            *
            {{
              $t("components.pricingSection.buyMoreCredits", {
                credits: BONUS_LIMIT,
                price: BONUS_LIMIT_PRICE,
              })
            }}
          </p>
          <p class="mt-2 text-xs text-subtle-foreground">
            {{ $t("components.pricingSection.lifetimeNote") }}
          </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Check } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { useProModal, type PricingPlan } from "@/store/useProModal";
import {
  LIFETIME_CREDITS,
  PRO_LIFETIME_PRICE_USD,
  PRO_MONTHLY_PRICE_USD,
  PRO_YEARLY_PRICE_USD,
} from "~/constants/pricing";

const props = withDefaults(
  defineProps<{
    hiddenInfo: boolean;
    modelValue: boolean;
    showTrialInfo?: boolean;
    /** Selected plan when rendered inside the upgrade modal. */
    plan?: PricingPlan;
  }>(),
  {
    hiddenInfo: false,
    modelValue: false,
    showTrialInfo: true,
    plan: "pro",
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "update:plan": [value: PricingPlan];
}>();

const { t, locale } = useI18n();
const { user } = useUser();

// On the 3-column layout each card is a subgrid spanning the same 7 rows
// (title, description, price, note, button, divider, features) so the buttons
// line up no matter how many lines a card's copy wraps to.
const cardGridClass = "lg:grid lg:grid-rows-subgrid lg:row-span-7 lg:gap-y-0";
// Smaller price inside the upgrade modal so the dialog fits without scrolling.
const priceSizeClass = computed(() => (props.hiddenInfo ? "text-4xl" : "text-5xl"));
const proModal = useProModal();

const isAnnual = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const plan = computed({
  get: () => props.plan,
  set: (value) => emit("update:plan", value),
});
// Inside the upgrade modal the single card re-skins itself as the Lifetime
// offer when that option is selected (price, credits line, feature list).
const modalLifetime = computed(() => props.hiddenInfo && plan.value === "lifetime");

// Landing page card: signed-in users go straight to the lifetime checkout
// modal, visitors sign up first (same as the other cards).
const onLifetimeClick = () => {
  if (user.value) {
    proModal.onOpen("lifetime");
  } else {
    navigateTo("/auth?view=sign_up");
  }
};

const freeFeatures = computed(() => [
  t("components.pricingSection.downloadSubtitles"),
  t("components.pricingSection.adFreeExperience"),
  t("components.pricingSection.uptime99"),
  t("components.pricingSection.limitedFeatures"),
]);

const premiumFeatures = computed(() => [
  t("components.pricingSection.downloadMultipleSubtitles"),
  t("components.pricingSection.autoTranslateSubtitles"),
  t("components.pricingSection.downloadPlaylistSubtitles"),
  t("components.pricingSection.autoYoutubeSubtitle"),
  t("components.pricingSection.videoAudioToSubtitle"),
  props.showTrialInfo
    ? t("components.pricingSection.apiAccess", { credits: TRIAL_CREDITS, monthly: MONTHLY_LIMIT }) + " *"
    : t("components.pricingSection.apiAccessNoTrial", { monthly: MONTHLY_LIMIT }) + " *",
  t("components.pricingSection.summarizeSubtitles"),
  t("common.analyze_comments"),
  t("components.pricingSection.useAllFeatures"),
  t("components.pricingSection.prioritySupport"),
]);

// Same features as Pro; the API line quotes the one-time grant instead of a
// monthly allowance.
const lifetimeFeatures = computed(() => [
  t("components.pricingSection.downloadMultipleSubtitles"),
  t("components.pricingSection.autoTranslateSubtitles"),
  t("components.pricingSection.downloadPlaylistSubtitles"),
  t("components.pricingSection.autoYoutubeSubtitle"),
  t("components.pricingSection.videoAudioToSubtitle"),
  t("components.pricingSection.lifetimeApiAccess", {
    credits: formatNumber(LIFETIME_CREDITS, locale.value),
  }) + " *",
  t("components.pricingSection.summarizeSubtitles"),
  t("common.analyze_comments"),
  t("components.pricingSection.useAllFeatures"),
  t("components.pricingSection.prioritySupport"),
]);
</script>
